import React, { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Plus, Minus, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import { getLanding, LANDINGS } from "@/data/landings";
import { NitaBadge } from "@/components/NitaBadge";

const NITA_CREDENTIAL = {
  "@type": "EducationalOccupationalCredential",
  name: "NITA-U Certified IT Provider (CCL2)",
  credentialCategory: "certification",
  recognizedBy: { "@type": "GovernmentOrganization", name: "National Information Technology Authority – Uganda", url: "https://www.nita.go.ug/" },
};

const SITE = "https://www.jero-onetechnologies.com";

function Faq({ item, open, onToggle, idx }) {
  return (
    <div className="border border-brand/10 rounded-xl overflow-hidden bg-white" data-testid={`landing-faq-${idx}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-brand-50/50 transition-colors">
        <span className="font-heading font-semibold text-brand">{item.q}</span>
        {open ? <Minus className="h-5 w-5 text-brand-500 shrink-0" /> : <Plus className="h-5 w-5 text-brand-500 shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 text-muted-foreground leading-relaxed">{item.a}</div>}
    </div>
  );
}

export default function ServiceLanding({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const navigate = useNavigate();
  const data = getLanding(slug);
  const [open, setOpen] = useState(0);

  if (!data) return <Navigate to="/" replace />;
  const isUganda = !data.phone || /uganda|kampala|entebbe/.test(data.slug);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.h1,
    serviceType: data.serviceType,
    description: data.metaDescription,
    areaServed: ["Uganda", "East Africa", "Africa", "North America", "Europe"],
    provider: {
      "@type": "Organization",
      name: "Jero-One Technologies Inc.",
      url: SITE,
      telephone: (data.phone || "+256 766 553 449").replace(/[^+\d]/g, ""),
      ...(isUganda && { hasCredential: NITA_CREDENTIAL }),
    },
    url: `${SITE}/${data.slug}`,
    mainEntity: {
      "@type": "FAQPage",
      mainEntity: data.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  };

  return (
    <div className="pt-16 lg:pt-20" data-testid={`landing-${data.slug}`}>
      <Seo
        title={data.metaTitle}
        description={data.metaDescription}
        keywords={data.keywords}
        path={`/${data.slug}`}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative bg-obsidian text-white overflow-hidden">
        <img src={data.image} alt={data.h1} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/90 to-obsidian/60" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <p className="overline text-brand-300 mb-3">{data.kicker}</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-4xl">{data.h1}</h1>
          <p className="mt-5 text-white/75 max-w-2xl text-lg leading-relaxed">{data.intro}</p>
          {isUganda && <NitaBadge className="mt-6" />}
          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="landing-quote-btn" onClick={() => navigate("/contact")} className="rounded-full bg-brand hover:bg-brand-700 text-white h-12 px-7">
              Get a Free Quote <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href={`tel:${(data.phone || "+256 766 553 449").replace(/[^+\d]/g, "")}`}>
              <Button variant="outline" className="rounded-full border-white/30 bg-white/5 text-white hover:bg-white/10 h-12 px-7">
                <Phone className="mr-2 h-4 w-4" /> {data.phone || "+256 766 553 449"}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-20 lg:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <p className="overline text-brand-500 mb-3">What we offer</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-brand mb-6">Complete, reliable solutions.</h2>
            <ul className="space-y-3">
              {data.offerings.map((o) => (
                <li key={o} className="flex items-start gap-3 text-foreground/80">
                  <span className="mt-1 h-5 w-5 rounded-full bg-brand-50 flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5 text-brand-500" />
                  </span>
                  {o}
                </li>
              ))}
            </ul>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-brand text-white rounded-2xl p-8 h-fit">
            <h3 className="font-heading font-bold text-xl mb-5">Why choose Jero-One Technologies?</h3>
            <ul className="space-y-3">
              {data.why.map((w) => (
                <li key={w} className="flex items-start gap-3 text-white/85">
                  <Check className="h-4 w-4 text-brand-300 shrink-0 mt-1" /> {w}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-white/60 text-sm">Founded in 2009 · Serving Uganda (Kampala & Entebbe) since 2016, plus the USA and Canada.</p>
          </motion.div>
        </div>
      </section>

      {data.process && (
        <section className="py-20 bg-obsidian text-white" data-testid="landing-process">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <p className="overline text-brand-300 mb-3">How we deliver</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mb-10">From first call to go-live.</h2>
            <ol className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {data.process.map((p, i) => (
                <li key={p.t} className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid={`landing-step-${i}`}>
                  <div className="font-mono text-xs text-brand-300">STEP {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-2 font-heading text-lg font-bold">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{p.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 bg-brand-50/50">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <p className="overline text-brand-500 mb-3">FAQs</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-brand">Frequently asked questions.</h2>
          </div>
          <div className="space-y-3">
            {data.faq.map((item, i) => (
              <Faq key={item.q} idx={i} item={item} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Button onClick={() => navigate("/contact")} className="rounded-full bg-brand hover:bg-brand-700 text-white h-12 px-8">Talk to our team</Button>
          </div>
        </div>
      </section>

      {/* Related solutions (internal linking for SEO) */}
      <section className="py-16 bg-background border-t border-brand/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="overline text-brand-500 mb-6">Explore more solutions</p>
          <div className="flex flex-wrap gap-3">
            {LANDINGS.filter((l) => l.slug !== data.slug).map((l) => (
              <button
                key={l.slug}
                data-testid={`related-${l.slug}`}
                onClick={() => { navigate(`/${l.slug}`); window.scrollTo(0, 0); }}
                className="text-sm font-medium bg-brand-50 text-brand px-4 py-2 rounded-full hover:bg-brand-100 transition-colors"
              >
                {l.kicker}
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
